<script setup lang="ts">
import { watch, ref } from 'vue';
import { getTranslations } from '@/lib/util/i18n';
import { CoButton } from '@deputy/copilot2';
import {
  AUTO_LOGOUT_TIMER_DURATION_IN_SECONDS,
  useUserStore,
} from '@/stores/user';
import LoadingSpinner from './LoadingSpinner.vue';
import ShiftActionError from '@/components/ShiftActionError.vue';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'shift-action-status',
});

const user = useUserStore();

const logoutTimer = ref(AUTO_LOGOUT_TIMER_DURATION_IN_SECONDS);
let logoutTimeout: ReturnType<typeof setTimeout>;

const props = defineProps<{
  isLoading: boolean;
  hasError: boolean;
  image: string;
  title: string;
  message?: string;
  status?: string;
}>();

watch(
  () => props.isLoading == false,
  () => {
    clearInterval(logoutTimeout);

    if (!props.hasError && logoutTimer.value >= 0) {
      logoutTimeout = setInterval(() => {
        logoutTimer.value--;
      }, 1000);
    }
  }
);

watch(
  () => user.data.activeUser,
  () => {
    clearInterval(logoutTimeout);
  }
);

const emit = defineEmits(['showShiftDetails', 'logout', 'reloadShiftCard']);
</script>

<template>
  <LoadingSpinner v-if="isLoading"></LoadingSpinner>
  <div v-else-if="!hasError">
    <div class="__shift-action-status">
      <div>
        <img :src="image" />
        <div class="__title" :class="`__title--${status}`">{{ title }}</div>
      </div>
      <div v-if="status == 'ending-shift'" class="__details">{{ message }}</div>
      <div class="__details">
        {{ t('logging-out', { logoutTimer: logoutTimer }) }}
      </div>
      <div class="__actions">
        <CoButton
          size="large"
          @click="emit('showShiftDetails')"
          data-test-id="back"
          >{{ t('back') }}</CoButton
        >
        <CoButton
          size="large"
          type="primary"
          @click="emit('logout')"
          data-test-id="logout"
          >{{ t('logout') }}</CoButton
        >
      </div>
    </div>
  </div>
  <ShiftActionError
    @logout="emit('logout')"
    @reloadShiftCard="emit('reloadShiftCard')"
    v-else
  ></ShiftActionError>
</template>

<style scoped>
.__shift-action-status {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

img {
  width: 100px;
  height: 100px;
  margin-bottom: 24px;
}
.__title {
  font-weight: 500;
  font-size: 30px;
  line-height: 30px;
  color: var(--co-text-primary);
  margin-bottom: 32px;
}
.__title--ending-shift {
  margin-bottom: 16px;
}
.__details {
  font-weight: 400;
  font-size: 17px;
  color: var(--co-text-primary);
  margin-bottom: 32px;
}
.__actions {
  width: 100%;
  display: flex;
  gap: 1rem;
}
.__actions .co-button {
  flex: 1;
}
</style>
